import {
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const CMD_ID = 0x80
const CMD_END = 0x81
const CMD_READ = 0x82
const CMD_WRITE = 0x83
const MEMORY_SIZE = 0x8000
const CHANNEL_SIZE = 16
const CHANNEL_COUNT = 999
const KG935G_WRITE_RANGES = [
  [0x0900, 64, 250],
  [0x4780, 32, 375],
  [0x7670, 8, 125],
] as const
const KG980P_WRITE_RANGES = [
  [0x004c, 12, 1],
  [0x0060, 16, 3],
  [0x0740, 40, 1],
  [0x0830, 16, 13],
  [0x0900, 8, 1],
  [0x0940, 64, 2],
  [0x0a00, 64, 249],
  [0x4840, 48, 1],
  [0x4900, 64, 124],
  [0x6800, 8, 7],
  [0x7000, 64, 15],
  [0x73c0, 39, 1],
] as const

type WouxunChannelFormat =
  | 'kg935g'
  | 'kg980p'
  | 'kguv8d'
  | 'kguv8dplus'

type KG935GSpec = {
  id: string
  model: string
  summary: string
  modelId: string
  recordStart: number
  powerLevels: readonly string[]
  zeroTxOff: boolean
  channelFormat: WouxunChannelFormat
  channelStart: number
  nameStart: number
  nameStride: number
  validStart: number
  validValue: number
  checksumMask: number
  encrypted: boolean
  writeRanges: readonly (readonly [number, number, number])[]
}

export type KG935GProfile = {
  protocol: 'wouxun-kg935g'
  id: string
  vendor: 'Wouxun'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 19200
  channelMinimum: 1
  channelMaximum: number
  nameLength: 8
  memorySize: number
  modelId: string
  recordStart: number
  powerLevels: readonly string[]
  zeroTxOff: boolean
  channelFormat: WouxunChannelFormat
  channelStart: number
  nameStart: number
  nameStride: number
  validStart: number
  validValue: number
  checksumMask: number
  encrypted: boolean
  writeRanges: readonly (readonly [number, number, number])[]
}

function kg935gFields() {
  return {
    channelFormat: 'kg935g' as const,
    channelStart: 0x0900,
    nameStart: 0x4780,
    nameStride: 12,
    validStart: 0x7670,
    validValue: 0x9e,
    checksumMask: 0xff,
    encrypted: true,
    writeRanges: KG935G_WRITE_RANGES,
  }
}

function kg980pFields() {
  return {
    channelFormat: 'kg980p' as const,
    channelStart: 0x09f0,
    nameStart: 0x48f8,
    nameStride: 8,
    validStart: 0x6fff,
    validValue: 0x00,
    checksumMask: 0x0f,
    encrypted: true,
    writeRanges: KG980P_WRITE_RANGES,
  }
}

function kguv8dFields() {
  return {
    channelFormat: 'kguv8d' as const,
    channelStart: 0x0900,
    nameStart: 0x4780,
    nameStride: 8,
    validStart: 0x6700,
    validValue: 0x9e,
    checksumMask: 0xff,
    encrypted: false,
    writeRanges: [[0x0000, 64, 512]] as const,
  }
}

function kguv8dPlusFields() {
  return {
    channelFormat: 'kguv8dplus' as const,
    channelStart: 0x0900,
    nameStart: 0x4780,
    nameStride: 12,
    validStart: 0x7670,
    validValue: 0x9e,
    checksumMask: 0xff,
    encrypted: true,
    writeRanges: [[0x0000, 64, 512]] as const,
  }
}

const SPECS: readonly KG935GSpec[] = [
  {
    id: 'wouxun-kg-935g',
    model: 'KG-935G',
    summary: 'Wouxun KG-935G GMRS handheld.',
    modelId: 'KG-UV8D-B',
    recordStart: 0x7c,
    powerLevels: ['Low', 'Medium', 'High'],
    zeroTxOff: false,
    ...kg935gFields(),
  },
  {
    id: 'wouxun-kg-935g-plus',
    model: 'KG-935G Plus',
    summary: 'Wouxun KG-935G Plus GMRS handheld.',
    modelId: 'KG-UV8D-B',
    recordStart: 0x7c,
    powerLevels: ['Low', 'Medium', 'High'],
    zeroTxOff: false,
    ...kg935gFields(),
  },
  {
    id: 'wouxun-kg-935h',
    model: 'KG-935H',
    summary: 'Wouxun KG-935H tri-band handheld.',
    modelId: 'KG-UV8D-B',
    recordStart: 0x7c,
    powerLevels: ['Low', 'Medium', 'High'],
    zeroTxOff: false,
    ...kg935gFields(),
  },
  {
    id: 'wouxun-kg-xs20g',
    model: 'KG-XS20G',
    summary: 'Wouxun KG-XS20G GMRS handheld.',
    modelId: 'KG-UV8D-A',
    recordStart: 0x79,
    powerLevels: ['Low', 'High'],
    zeroTxOff: true,
    ...kg935gFields(),
  },
  {
    id: 'wouxun-kg-xs20g-plus',
    model: 'KG-XS20G Plus',
    summary: 'Wouxun KG-XS20G Plus GMRS handheld.',
    modelId: 'KG-UV8D-A',
    recordStart: 0x79,
    powerLevels: ['Low', 'High'],
    zeroTxOff: true,
    ...kg935gFields(),
  },
  {
    id: 'wouxun-kg-xs20h',
    model: 'KG-XS20H',
    summary: 'Wouxun KG-XS20H tri-band handheld.',
    modelId: 'KG-UV8D-A',
    recordStart: 0x79,
    powerLevels: ['Low', 'High'],
    zeroTxOff: true,
    ...kg935gFields(),
  },
  {
    id: 'wouxun-kg-uv8h',
    model: 'KG-UV8H',
    summary: 'Wouxun KG-UV8H dual-band handheld.',
    modelId: 'KG-UV8D-B',
    recordStart: 0x7c,
    powerLevels: ['Low', 'Medium', 'High'],
    zeroTxOff: false,
    ...kg935gFields(),
  },
  {
    id: 'wouxun-kg-uv980p',
    model: 'KG-UV980P',
    summary: 'Wouxun KG-UV980P dual-band mobile.',
    modelId: 'KG-UV950R',
    recordStart: 0xda,
    powerLevels: ['Low', 'Medium', 'High'],
    zeroTxOff: false,
    ...kg980pFields(),
  },
  {
    id: 'wouxun-kg-1000g',
    model: 'KG-1000G',
    summary: 'Wouxun KG-1000G GMRS mobile.',
    modelId: 'KG-UV950R',
    recordStart: 0xda,
    powerLevels: ['Low', 'Medium', 'High'],
    zeroTxOff: false,
    ...kg980pFields(),
  },
  {
    id: 'wouxun-kg-1000g-plus',
    model: 'KG-1000G Plus',
    summary: 'Wouxun KG-1000G Plus GMRS mobile.',
    modelId: 'KG-UV950R',
    recordStart: 0xda,
    powerLevels: ['Low', 'Medium', 'High'],
    zeroTxOff: false,
    ...kg980pFields(),
  },
  {
    id: 'wouxun-kg-uv8d',
    model: 'KG-UV8D',
    summary: 'Wouxun KG-UV8D dual-band handheld.',
    modelId: 'KG-UV8D\u0000\u0000',
    recordStart: 0x7d,
    powerLevels: ['Low', 'High'],
    zeroTxOff: false,
    ...kguv8dFields(),
  },
  {
    id: 'wouxun-kg-uv8d-plus',
    model: 'KG-UV8D Plus',
    summary: 'Wouxun KG-UV8D Plus dual-band handheld.',
    modelId: 'KG-UV8D\u0000\u0000',
    recordStart: 0x7a,
    powerLevels: ['Low', 'High'],
    zeroTxOff: false,
    ...kguv8dPlusFields(),
  },
  {
    id: 'wouxun-kg-uv8e',
    model: 'KG-UV8E',
    summary: 'Wouxun KG-UV8E dual-band handheld.',
    modelId: 'KG-UV8D-A',
    recordStart: 0x7b,
    powerLevels: ['Low', 'High'],
    zeroTxOff: false,
    ...kguv8dPlusFields(),
  },
]

export const KG935G_PROFILES: readonly KG935GProfile[] = SPECS.map((spec) => ({
  protocol: 'wouxun-kg935g',
  id: spec.id,
  vendor: 'Wouxun',
  model: spec.model,
  summary: spec.summary,
  tags: ['Experimental', 'Read + write', '999 channels', '8-character names'],
  baudRate: 19200,
  channelMinimum: 1,
  channelMaximum: CHANNEL_COUNT,
  nameLength: 8,
  memorySize: MEMORY_SIZE,
  modelId: spec.modelId,
  recordStart: spec.recordStart,
  powerLevels: spec.powerLevels,
  zeroTxOff: spec.zeroTxOff,
  channelFormat: spec.channelFormat,
  channelStart: spec.channelStart,
  nameStart: spec.nameStart,
  nameStride: spec.nameStride,
  validStart: spec.validStart,
  validValue: spec.validValue,
  checksumMask: spec.checksumMask,
  encrypted: spec.encrypted,
  writeRanges: spec.writeRanges,
}))

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: 'Low',
    skip: false,
  }
}

function readUint16BE(raw: Uint8Array, offset: number): number {
  return (raw[offset] << 8) | raw[offset + 1]
}

function writeUint16BE(raw: Uint8Array, offset: number, value: number): void {
  raw[offset] = (value >>> 8) & 0xff
  raw[offset + 1] = value & 0xff
}

function readUint32BE(raw: Uint8Array, offset: number): number {
  return (
    ((raw[offset] << 24) |
      (raw[offset + 1] << 16) |
      (raw[offset + 2] << 8) |
      raw[offset + 3]) >>>
    0
  )
}

function writeUint32BE(raw: Uint8Array, offset: number, value: number): void {
  raw[offset] = (value >>> 24) & 0xff
  raw[offset + 1] = (value >>> 16) & 0xff
  raw[offset + 2] = (value >>> 8) & 0xff
  raw[offset + 3] = value & 0xff
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(value: number): DecodedTone {
  if (value === 0) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  if (value & 0x4000) {
    return {
      mode: 'dtcs',
      ctcss: null,
      dtcs: Number((value & 0x01ff).toString(8)),
      polarity: value & 0x2000 ? 'R' : 'N',
    }
  }
  return {
    mode: 'ctcss',
    ctcss: (value & 0x7fff) / 10,
    dtcs: null,
    polarity: 'N',
  }
}

function encodeTone(channel: WebChannel): number {
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    return 0x8000 + Math.round(channel.ctcss * 10)
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    let value = (Number.parseInt(channel.dtcs.toString(), 8) & 0x01ff) | 0x4000
    if (channel.dtcsPolarity === 'R') value |= 0x2000
    return value
  }
  return 0
}

function decodeBbcd(raw: Uint8Array): number {
  let value = 0
  for (const byte of raw) {
    value = value * 100 + (byte >> 4) * 10 + (byte & 0x0f)
  }
  return value
}

function encodeBbcd(value: number, length: number): Uint8Array {
  const result = new Uint8Array(length)
  let remaining = Math.max(0, Math.trunc(value))
  for (let index = length - 1; index >= 0; index -= 1) {
    const part = remaining % 100
    result[index] = (Math.floor(part / 10) << 4) | (part % 10)
    remaining = Math.floor(remaining / 100)
  }
  return result
}

function decodeKg980PTone(value: number): DecodedTone {
  if (value === 0 || value === 0xffff) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  if (value & 0x8000) {
    return {
      mode: 'dtcs',
      ctcss: null,
      dtcs: Number(String(value & 0x07ff).padStart(3, '0')),
      polarity: value & 0x4000 ? 'R' : 'N',
    }
  }
  return {
    mode: 'ctcss',
    ctcss: (value & 0x7fff) / 10,
    dtcs: null,
    polarity: 'N',
  }
}

function encodeKg980PTone(channel: WebChannel): number {
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    return Math.round(channel.ctcss * 10);
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    return (
      0x8000 |
      Number.parseInt(String(channel.dtcs).padStart(3, '0'), 10) |
      (channel.dtcsPolarity === 'R' ? 0x4000 : 0)
    );
  }
  return 0;
}

function decodeKguvTone(
  value: number,
  format: 'kguv8d' | 'kguv8dplus',
): DecodedTone {
  if (value === 0 || value === 0xffff) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  if (format === 'kguv8dplus') return decodeTone(value)
  if ((value & 0x2800) === 0x2800) {
    return {
      mode: 'dtcs',
      ctcss: null,
      dtcs: Number((value & 0x07ff).toString(8)),
      polarity: value & 0x8000 ? 'R' : 'N',
    }
  }
  return {
    mode: 'ctcss',
    ctcss: (value & 0x7fff) / 10,
    dtcs: null,
    polarity: 'N',
  }
}

function encodeKguvTone(
  channel: WebChannel,
  format: 'kguv8d' | 'kguv8dplus',
): number {
  if (format === 'kguv8dplus') return encodeTone(channel)
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    return Math.round(channel.ctcss * 10)
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    let value =
      (Number.parseInt(channel.dtcs.toString(), 8) & 0x07ff) |
      0x2800
    if (channel.dtcsPolarity === 'R') value |= 0x8000
    return value
  }
  return 0
}

function decodeName(
  image: Uint8Array,
  profile: KG935GProfile,
  number: number,
): string {
  const start = profile.nameStart + number * profile.nameStride;
  let name = ''
  for (let index = 0; index < Math.min(8, profile.nameStride); index += 1) {
    const value = image[start + index]
    if (!value) break
    name += String.fromCharCode(value)
  }
  return name.trim()
}

export function parseKG935GChannel(
  image: Uint8Array,
  profile: KG935GProfile,
  number: number,
): WebChannel {
  if (image[profile.validStart + number] !== profile.validValue) {
    return emptyChannel(number)
  }
  const start = profile.channelStart + number * CHANNEL_SIZE
  const raw = image.slice(start, start + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE) return emptyChannel(number)
  if (
    profile.channelFormat === 'kguv8d' ||
    profile.channelFormat === 'kguv8dplus'
  ) {
    const rxValue = readUint32BE(raw, 0)
    if (!rxValue || rxValue === 0xffffffff) {
      return emptyChannel(number)
    }
    const rxFrequency = rxValue * 10
    const txValue = readUint32BE(raw, 4)
    const txFrequency =
      txValue === 0xffffffff ? null : txValue * 10
    const txTone = decodeKguvTone(
      readUint16BE(raw, 10),
      profile.channelFormat,
    )
    const rxTone = decodeKguvTone(
      readUint16BE(raw, 8),
      profile.channelFormat,
    )
    const selected = txTone.mode === 'none' ? rxTone : txTone
    return {
      number,
      empty: false,
      name: decodeName(image, profile, number),
      rxFrequency,
      txFrequency,
      toneMode: selected.mode,
      ctcss: selected.ctcss,
      dtcs: selected.dtcs,
      dtcsPolarity: selected.polarity,
      mode: raw[13] & 0x01 ? 'FM' : 'NFM',
      power: (raw[12] & 0x02) !== 0 ? 'High' : 'Low',
      skip: (raw[13] & 0x20) === 0,
    }
  }
  if (profile.channelFormat === 'kg980p') {
    const rxFrequency = decodeBbcd(raw.slice(0, 4)) * 10
    if (!rxFrequency) return emptyChannel(number)
    const txRaw = raw.slice(4, 8)
    const txFrequency = txRaw.every((value) => value === 0xff)
      ? null
      : decodeBbcd(txRaw) * 10
    const txTone = decodeKg980PTone(readUint16BE(raw, 8))
    const rxTone = decodeKg980PTone(readUint16BE(raw, 10))
    const selected = txTone.mode === 'none' ? rxTone : txTone
    const powerCode = (raw[12] >> 3) & 0x03
    return {
      number,
      empty: false,
      name:
        raw[12] & 0x80
          ? decodeName(image, profile, number)
          : '',
      rxFrequency,
      txFrequency,
      toneMode: selected.mode,
      ctcss: selected.ctcss,
      dtcs: selected.dtcs,
      dtcsPolarity: selected.polarity,
      mode: raw[12] & 0x04
        ? raw[12] & 0x01
          ? 'NAM'
          : 'AM'
        : raw[12] & 0x01
          ? 'NFM'
          : 'FM',
      power: profile.powerLevels[Math.min(powerCode, 2)] ?? 'Low',
      skip: (raw[12] & 0x40) === 0,
    }
  }
  const rxFrequency = readUint32BE(raw, 0) * 10
  if (!rxFrequency) return emptyChannel(number)
  const txValue = readUint32BE(raw, 4)
  const txFrequency =
    txValue === 0xffffffff || (profile.zeroTxOff && txValue === 0)
      ? null
      : txValue * 10
  const txTone = decodeTone(readUint16BE(raw, 10))
  const rxTone = decodeTone(readUint16BE(raw, 8))
  const selected = txTone.mode === 'none' ? rxTone : txTone
  const powerCode = raw[12] & 0x0f
  const power =
    profile.powerLevels.length === 2
      ? profile.powerLevels[powerCode === 0 ? 0 : 1]
      : profile.powerLevels[Math.min(powerCode, profile.powerLevels.length - 1)]
  return {
    number,
    empty: false,
    name: decodeName(image, profile, number),
    rxFrequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode: raw[13] & 0x01 ? 'FM' : 'NFM',
    power,
    skip: (raw[13] & 0x20) === 0,
  }
}

export function parseKG935GChannels(
  image: Uint8Array,
  profile: KG935GProfile,
): WebChannel[] {
  return Array.from({ length: CHANNEL_COUNT }, (_, index) =>
    parseKG935GChannel(image, profile, index + 1),
  )
}

export function applyKG935GChannel(
  image: Uint8Array,
  profile: KG935GProfile,
  channel: WebChannel,
): void {
  const start = profile.channelStart + channel.number * CHANNEL_SIZE
  const raw = image.subarray(start, start + CHANNEL_SIZE)
  const nameStart = profile.nameStart + channel.number * profile.nameStride
  if (channel.empty) {
    raw.fill(profile.channelFormat === 'kg980p' ? 0xff : 0)
    image.fill(
      profile.channelFormat === 'kg980p' ? 0xff : 0,
      nameStart,
      nameStart + profile.nameStride,
    )
    image[profile.validStart + channel.number] =
      profile.channelFormat === 'kg980p' ? 0xff : 0
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  if (
    profile.channelFormat === 'kguv8d' ||
    profile.channelFormat === 'kguv8dplus'
  ) {
    writeUint32BE(raw, 0, Math.round(channel.rxFrequency / 10))
    writeUint32BE(
      raw,
      4,
      channel.txFrequency
        ? Math.round(channel.txFrequency / 10)
        : 0xffffffff,
    )
    const tone = encodeKguvTone(channel, profile.channelFormat)
    writeUint16BE(raw, 8, tone)
    writeUint16BE(raw, 10, tone)
    raw[12] =
      (raw[12] & 0xfd) | (channel.power === 'High' ? 0x02 : 0)
    raw[13] =
      (raw[13] & 0xde) |
      (channel.skip ? 0 : 0x20) |
      (channel.mode === 'FM' ? 0x01 : 0)
    image.fill(0, nameStart, nameStart + profile.nameStride)
    const name = channel.name
      .replace(/[^\x20-\x7e]/g, '')
      .slice(0, 8)
    for (let index = 0; index < name.length; index += 1) {
      image[nameStart + index] = name.charCodeAt(index)
    }
    image[profile.validStart + channel.number] = profile.validValue
    return
  }
  if (profile.channelFormat === 'kg980p') {
    encodeBbcd(Math.round(channel.rxFrequency / 10), 4).forEach(
      (value, index) => {
        raw[index] = value
      },
    )
    if (channel.txFrequency) {
      encodeBbcd(Math.round(channel.txFrequency / 10), 4).forEach(
        (value, index) => {
          raw[4 + index] = value
        },
      )
    } else {
      raw.fill(0xff, 4, 8)
    }
    const tone = encodeKg980PTone(channel)
    writeUint16BE(raw, 8, tone)
    writeUint16BE(raw, 10, tone)
    const powerCode =
      channel.power === 'High' ? 2 : channel.power === 'Medium' ? 1 : 0
    raw[12] =
      (raw[12] & 0x10) |
      (channel.name ? 0x80 : 0) |
      (channel.skip ? 0 : 0x40) |
      ((powerCode & 0x03) << 3) |
      (channel.mode === 'AM' || channel.mode === 'NAM' ? 0x04 : 0) |
      (channel.mode === 'NFM' || channel.mode === 'NAM' ? 0x01 : 0)
    image.fill(0, nameStart, nameStart + profile.nameStride)
    const name = channel.name
      .replace(/[^\x20-\x7e]/g, '')
      .slice(0, 8)
    for (let index = 0; index < name.length; index += 1) {
      image[nameStart + index] = name.charCodeAt(index)
    }
    image[profile.validStart + channel.number] = profile.validValue
    return
  }
  writeUint32BE(raw, 0, Math.round(channel.rxFrequency / 10))
  writeUint32BE(
    raw,
    4,
    channel.txFrequency
      ? Math.round(channel.txFrequency / 10)
      : profile.zeroTxOff
        ? 0
        : 0xffffffff,
  )
  const tone = encodeTone(channel)
  writeUint16BE(raw, 8, tone)
  writeUint16BE(raw, 10, tone)
  const powerIndex =
    profile.powerLevels.length === 2
      ? channel.power === 'Low'
        ? 0
        : 2
      : Math.max(0, profile.powerLevels.indexOf(channel.power))
  raw[12] = (raw[12] & 0xf0) | (powerIndex & 0x0f)
  raw[13] =
    (raw[13] & 0xde) |
    (channel.skip ? 0 : 0x20) |
    (channel.mode === 'FM' ? 0x01 : 0)
  raw[14] = raw[14] || 1
  raw[15] = raw[15] || 0xff
  image.fill(0, nameStart, nameStart + profile.nameStride)
  const name = channel.name
    .replace(/[^\x20-\x7e]/g, '')
    .slice(0, 8)
  for (let index = 0; index < name.length; index += 1) {
    image[nameStart + index] = name.charCodeAt(index)
  }
  image[profile.validStart + channel.number] = profile.validValue
}

function checksum(data: Uint8Array): number {
  let total = 0
  for (const value of data) total = (total + value) & 0xff
  return total
}

function encrypt(data: Uint8Array): Uint8Array {
  const result = new Uint8Array(data.length)
  result[0] = 0x57 ^ data[0]
  for (let index = 1; index < data.length; index += 1) {
    result[index] = result[index - 1] ^ data[index]
  }
  return result
}

function decrypt(data: Uint8Array): Uint8Array {
  const result = new Uint8Array(data.length)
  for (let index = 0; index < data.length; index += 1) {
    result[index] =
      data[index] ^ (index === 0 ? 0x57 : data[index - 1])
  }
  return result
}

async function writeRecord(
  profile: KG935GProfile,
  transport: RadioTransport,
  command: number,
  payload: Uint8Array = new Uint8Array(0),
): Promise<void> {
  const header = Uint8Array.of(
    profile.recordStart,
    command,
    0xff,
    payload.length,
  )
  const plain = new Uint8Array(payload.length + 1)
  plain.set(payload)
  plain[payload.length] =
    checksum(Uint8Array.from([...header.slice(1), ...payload])) &
    profile.checksumMask
  await transport.write(
    new Uint8Array([
      ...header,
      ...(profile.encrypted ? encrypt(plain) : plain),
    ]),
  )
}

async function readRecord(
  profile: KG935GProfile,
  transport: RadioTransport,
): Promise<Uint8Array> {
  const header = await transport.readExact(4, 3500)
  if (header[0] !== profile.recordStart) {
    throw new Error(
      `Unexpected Wouxun record marker 0x${header[0].toString(16)}.`,
    )
  }
  const response = await transport.readExact(header[3], 4000)
  const responseChecksum = (await transport.readExact(1, 2000))[0]
  const payload = profile.encrypted ? decrypt(response) : response
  const received = profile.encrypted
    ? (responseChecksum ^ response.at(-1)!) & profile.checksumMask
    : responseChecksum & profile.checksumMask
  const expected =
    (checksum(header.slice(1)) + checksum(payload)) &
    profile.checksumMask
  if (received !== expected) throw new Error('Wouxun record checksum failed.')
  return payload
}

async function enter(
  profile: KG935GProfile,
  transport: RadioTransport,
): Promise<void> {
  await transport.discardInput()
  for (let attempt = 0; attempt < 10; attempt += 1) {
    await writeRecord(profile, transport, CMD_ID)
    try {
      const identity = await readRecord(profile, transport)
      const model = new TextDecoder().decode(identity.slice(0, 9))
      if (model === profile.modelId) return
    } catch {
      // The first identity reply can intentionally have a bad checksum.
    }
  }
  throw new Error(`The radio did not identify as ${profile.model}.`)
}

function addressPayload(address: number, length?: number): Uint8Array {
  const result = new Uint8Array(length === undefined ? 2 : 3)
  result[0] = (address >>> 8) & 0xff
  result[1] = address & 0xff
  if (length !== undefined) result[2] = length
  return result
}

export async function readKG935GRadioWithTransport(
  profile: KG935GProfile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enter(profile, transport)
  try {
    const image = new Uint8Array(MEMORY_SIZE)
    const blockSize = 64
    const total = MEMORY_SIZE / blockSize
    for (let index = 0; index < total; index += 1) {
      const address = index * blockSize
      await writeRecord(
        profile,
        transport,
        CMD_READ,
        addressPayload(address, blockSize),
      )
      const response = await readRecord(profile, transport)
      image.set(response.slice(2), address)
      onProgress?.({
        current: index + 1,
        total,
        label: `Reading ${profile.model} memory`,
      })
    }
    return { image, reportedModel: profile.model }
  } finally {
    await writeRecord(profile, transport, CMD_END)
  }
}

export async function writeKG935GRadioWithTransport(
  profile: KG935GProfile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) {
    throw new Error('The selected image is smaller than KG-935G memory.')
  }
  await enter(profile, transport)
  try {
    const writes: Array<{ address: number; size: number }> = []
    for (const [start, size, count] of profile.writeRanges) {
      for (let index = 0; index < count; index += 1) {
        writes.push({ address: start + index * size, size })
      }
    }
    for (let index = 0; index < writes.length; index += 1) {
      const { address, size } = writes[index]
      const payload = new Uint8Array(2 + size)
      payload.set(addressPayload(address))
      payload.set(image.slice(address, address + size), 2)
      await writeRecord(profile, transport, CMD_WRITE, payload)
      const response = await readRecord(profile, transport)
      if (((response[0] << 8) | response[1]) !== address) {
        throw new Error(`Wouxun did not acknowledge block 0x${address.toString(16)}.`)
      }
      onProgress?.({
        current: index + 1,
        total: writes.length,
        label: `Writing ${profile.model} memory`,
      })
    }
  } finally {
    await writeRecord(profile, transport, CMD_END)
  }
}
